import { downloadDocument } from "../common";

const CSV_MIME_TYPE = "text/csv;charset=utf-8;";

/** UTF-8 byte-order mark (U+FEFF). Without it Excel reads the file as the local codepage. */
const UTF8_BOM = String.fromCharCode(0xfeff);

/**
 * Leading characters that make a spreadsheet treat a cell as a formula rather than as text. Tab and
 * carriage return are included because Excel strips them and then re-reads what follows.
 */
const FORMULA_LEADING_CHARACTER = /^[=+\-@\t\r]/;

/** A negative number — the one kind of real data that legitimately opens with a formula character. */
const NEGATIVE_NUMBER = /^-\d/;

/**
 * Defuse a field a spreadsheet would otherwise execute. Exported values are user-authored (task and
 * object names, tags, assignee names), so a name beginning `=`, `+`, `-` or `@` becomes a live formula
 * when someone opens the file — quoting does not prevent that, because the quotes are consumed by the
 * CSV parse before the cell is evaluated. Prefixing with an apostrophe marks the cell as text.
 *
 * Only negative numbers are exempt. Anything else is defused even where the spreadsheet's own reading
 * would be harmless, because that reading still loses the value: a tag named "+1" would otherwise
 * arrive as `1`.
 */
const neutraliseFormula = (text: string): string => {
  if (NEGATIVE_NUMBER.test(text) && !Number.isNaN(Number(text))) return text;

  return FORMULA_LEADING_CHARACTER.test(text) ? `'${text}` : text;
};

/**
 * Escape one CSV field per RFC 4180: a field containing a comma, a quote or a newline is wrapped in
 * quotes, and any quote inside it is doubled. Without this a task name with a comma in it silently
 * shifts every following column of that row. Formula-leading values are defused first — see
 * `neutraliseFormula`.
 */
export const escapeCsvValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "";

  const text = typeof value === "number" ? String(value) : neutraliseFormula(value);

  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Header row + data rows → CSV text. Rows are emitted in the order given. */
export const toCsvContent = (headers: string[], rows: (string | number | null | undefined)[][]): string =>
  [headers, ...rows].map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");

/**
 * Download CSV text as a file. A BOM is prepended so Excel opens the file as UTF-8 rather than as
 * the local codepage, which is what mangles non-ASCII names.
 */
export const downloadCsv = (filename: string, content: string): void => {
  const url = window.URL.createObjectURL(new Blob([`${UTF8_BOM}${content}`], { type: CSV_MIME_TYPE }));

  try {
    downloadDocument(filename, CSV_MIME_TYPE, url, false);
  } finally {
    window.URL.revokeObjectURL(url);
  }
};
